import React from 'react';
import SafeIcon from '../common/SafeIcon.jsx';
import * as FiIcons from 'react-icons/fi';
import { aspectRatioOptions } from '../data/vehicleData.js';

const { FiX, FiDownload, FiShare2, FiCopy, FiMaximize2, FiEye, FiAlertCircle } = FiIcons;

const ImageModal = ({ 
  isOpen, 
  onClose, 
  imageResult, 
  aspectRatio, 
  textOverlays, 
  prompt 
}) => {
  if (!isOpen) return null;

  const aspectRatioInfo = aspectRatioOptions.find(option => option.id === aspectRatio);
  const visibleOverlays = textOverlays?.filter(overlay => overlay.visible) || [];

  const handleDownload = () => {
    if (imageResult && imageResult.imageUrl) {
      const link = document.createElement('a');
      link.href = imageResult.imageUrl;
      link.download = `vehicle-marketing-${Date.now()}.${imageResult.mimeType?.split('/')[1] || 'png'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCopyPrompt = async () => {
    if (prompt) {
      try {
        await navigator.clipboard.writeText(prompt);
        alert('Prompt copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy prompt:', err);
      }
    }
  };

  const handleShare = async () => {
    if (navigator.share && imageResult && imageResult.imageUrl) {
      try {
        await navigator.share({
          title: 'Vehicle Marketing Image',
          text: 'Check out this marketing image generated for our dealership!',
          url: imageResult.imageUrl
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl max-h-[90vh] w-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <SafeIcon icon={FiMaximize2} className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Marketing Image Preview</h2>
              {aspectRatioInfo && (
                <p className="text-sm text-gray-500">
                  {aspectRatioInfo.name} • {aspectRatioInfo.dimensions} • {aspectRatioInfo.description}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Action Buttons */}
            <button
              onClick={handleCopyPrompt}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Copy prompt"
            >
              <SafeIcon icon={FiCopy} className="w-5 h-5" />
            </button>
            
            {navigator.share && (
              <button
                onClick={handleShare}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Share image"
              >
                <SafeIcon icon={FiShare2} className="w-5 h-5" />
              </button>
            )}
            
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              title="Download image"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              <span>Download</span>
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Image Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {imageResult ? (
              <div className="space-y-4">
                {/* Main Image */}
                <div className="relative bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  <img
                    src={imageResult.imageUrl}
                    alt="Generated marketing image"
                    className="max-w-full max-h-[60vh] object-contain"
                    style={{
                      aspectRatio: aspectRatioInfo?.ratio || 'auto'
                    }}
                  />
                  
                  {/* Text Overlay Preview */}
                  {visibleOverlays.length > 0 && (
                    <div className="absolute inset-0 pointer-events-none">
                      {visibleOverlays.map((overlay) => {
                        let overlayText = overlay.text;
                        if (overlay.type === 'offer' && overlay.template && overlay.fields) {
                          overlayText = overlay.template.template;
                          overlay.template.fields.forEach(field => {
                            const value = overlay.fields[field] || `[${field}]`;
                            overlayText = overlayText.replace(`{{${field}}}`, value);
                          });
                        }

                        const getPositionClasses = (position) => {
                          const positions = {
                            'top-left': 'top-4 left-4',
                            'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
                            'top-right': 'top-4 right-4',
                            'center-left': 'top-1/2 left-4 transform -translate-y-1/2',
                            'center': 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
                            'center-right': 'top-1/2 right-4 transform -translate-y-1/2',
                            'bottom-left': 'bottom-4 left-4',
                            'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
                            'bottom-right': 'bottom-4 right-4'
                          };
                          return positions[position] || positions['bottom-right'];
                        };

                        const getFontSize = (size) => {
                          const sizes = {
                            'small': 'text-lg',
                            'medium': 'text-xl',
                            'large': 'text-2xl',
                            'xl': 'text-3xl'
                          };
                          return sizes[size] || sizes['large'];
                        };

                        const getFontStyle = (style) => {
                          const styles = {
                            'bold': 'font-bold',
                            'elegant': 'font-light italic',
                            'modern': 'font-medium',
                            'classic': 'font-serif',
                            'dynamic': 'font-bold uppercase tracking-wide'
                          };
                          return styles[style] || styles['bold'];
                        };

                        return (
                          <div
                            key={overlay.id}
                            className={`absolute ${getPositionClasses(overlay.position)} ${getFontSize(overlay.fontSize)} ${getFontStyle(overlay.fontStyle)} text-white px-4 py-2 rounded shadow-lg max-w-md`}
                            style={{
                              color: overlay.color,
                              backgroundColor: overlay.backgroundColor !== 'transparent' ? overlay.backgroundColor : undefined,
                              textShadow: overlay.backgroundColor === 'transparent' ? '2px 2px 4px rgba(0,0,0,0.8)' : undefined
                            }}
                          >
                            {overlayText || 'Sample Text'}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Image Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {imageResult.isPlaceholder ? (
                        <div className="flex items-center space-x-2 text-amber-600">
                          <SafeIcon icon={FiAlertCircle} className="w-4 h-4" />
                          <span className="text-sm font-medium">Preview Image</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 text-green-600">
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                          <span className="text-sm font-medium">Generated Image</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-500">
                      {aspectRatioInfo?.dimensions}
                    </div>
                  </div>

                  {/* Additional Info */}
                  {imageResult.note && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                      <p className="text-sm text-amber-800">{imageResult.note}</p>
                    </div>
                  )}

                  {imageResult.error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-800">{imageResult.error}</p>
                    </div>
                  )}
                </div>

                {/* Text Overlays Summary */}
                {visibleOverlays.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-3">Active Text Overlays:</h4>
                    <div className="space-y-2">
                      {visibleOverlays.map((overlay) => {
                        let overlayText = overlay.text;
                        if (overlay.type === 'offer' && overlay.template && overlay.fields) {
                          overlayText = overlay.template.template;
                          overlay.template.fields.forEach(field => {
                            const value = overlay.fields[field] || `[${field}]`;
                            overlayText = overlayText.replace(`{{${field}}}`, value);
                          });
                        }
                        
                        return (
                          <div key={overlay.id} className="flex items-center justify-between text-sm">
                            <span className="text-blue-700 font-medium truncate flex-1">
                              "{overlayText || 'Sample Text'}"
                            </span>
                            <div className="flex items-center space-x-2 ml-4 text-blue-600">
                              <span className="capitalize text-xs">
                                {overlay.position.replace('-', ' ')}
                              </span>
                              <span className="text-blue-400">•</span>
                              <span className="capitalize text-xs">
                                {overlay.fontSize}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-12 text-gray-500">
                <SafeIcon icon={FiEye} className="w-16 h-16 mb-4" />
                <p className="text-lg">No image to display</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;